import "./instrument";
import "./main.scss";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/de";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import minMax from "dayjs/plugin/minMax";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "~/components/ui/sonner";
import UserStoreHydrator from "~/components/user/UserStoreHydrator";
import { trpc, trpcClient } from "./api/trpc";
import App from "./App";
import AuthProvider from "./auth/authProvider";
import { isAuthError, isUnauthorizedError } from "~/lib/is-auth-error";
import "./index.css";

dayjs.locale("de");
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(minMax);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.tz.setDefault("Europe/Berlin");

const handleAuthError = (error: unknown) => {
  // Only log out on authentication failures (UNAUTHORIZED). FORBIDDEN means the
  // session is valid but the action is not permitted, which must NOT log the user out.
  if (isUnauthorizedError(error)) window.dispatchEvent(new Event("auth:unauthorized"));
};

// Create a client for react-query
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
  defaultOptions: {
    queries: { retry: (failureCount, error) => !isAuthError(error) && failureCount < 3 },
  },
});

const root = createRoot(document.getElementById("root")!);
if (window.location.hostname.includes("admin.samson.kalialab.de")) {
  window.location.href = "https://admin.booking.kalialab.de";
}
root.render(
  <React.StrictMode>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
            <Toaster />
            <UserStoreHydrator />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  </React.StrictMode>,
);
